import React, { useMemo } from "react"
import { Box, Divider } from "@material-ui/core"
import moment from "moment"
import { PaperBox, RowBox } from "../Boxes"
import { InfoSection } from "../Viewers"
import { useQueryIntegrations, USER_STATUS } from "../../data"
import TimeAgo from "../TimeAgo"
import { LinkButton } from "../LinkButton"
import { FormatDateTimeCompact } from "../Format"
import { Caption, FieldSectionHeading } from "../Headings"
import { toId } from "../../utils"
import { PersonNotes } from "./PersonNotes"

const PersonDetails = ({ user, onResendInvite }) => {
  const { data: integrationsData } = useQueryIntegrations()
  const isInitial = USER_STATUS.INITIAL === user.status
  const isInvite = [USER_STATUS.INVITED, USER_STATUS.INITIAL].includes(user.status)
  const isInviteExpired = isInvite && moment().isAfter(user.invite.expiresAt)

  const handleResendInvite = () => {
    onResendInvite && onResendInvite()
  }

  const hasAttributes = useMemo(
    () => integrationsData?.integrations.some(({ integrationProvider: { attributes } }) => attributes?.user.length),
    [integrationsData]
  )

  return (
    <>
      <FieldSectionHeading>Account &amp; login</FieldSectionHeading>
      <PaperBox mb={3}>
        <InfoSection title="Email address" value={user.email} />
        {isInvite && (
          <>
            <Divider />
            <InfoSection
              title="Invitation"
              value={
                <RowBox>
                  <LinkButton onClick={handleResendInvite}>
                    {isInitial && <>Send invite</>}
                    {!isInitial && <>Resend invite</>}
                  </LinkButton>
                  {isInviteExpired && (
                    <Box ml={1}>
                      Expired <FormatDateTimeCompact value={user.invite.expiresAt} />
                    </Box>
                  )}
                  {!isInviteExpired && Boolean(user.invite?.sentAt) && (
                    <Box ml={1}>
                      Sent <TimeAgo date={user.invite.sentAt} lowercase />, expires{" "}
                      <FormatDateTimeCompact value={user.invite.expiresAt} />
                    </Box>
                  )}
                </RowBox>
              }
            />
          </>
        )}
        {user.external && (
          <>
            <Divider />
            <InfoSection title="Identity provider" value={user.identityProvider.name} />
          </>
        )}
      </PaperBox>
      <FieldSectionHeading>Work, locations &amp; permissions</FieldSectionHeading>
      <PaperBox mb={3}>
        <InfoSection title="Areas of Work" value={user.groups.map((group) => group.name).join(", ")} />
        <Divider />
        <InfoSection title="Locations" value={user.locations.map((location) => location.name).join(", ")} />
        <Divider />
        <InfoSection
          title="Access level"
          value={user.roles.map((role) => role.friendlyName).join(", ") || "Employee"}
        />
      </PaperBox>
      {hasAttributes && (
        <>
          <FieldSectionHeading>Integration attributes</FieldSectionHeading>
          <PaperBox mb={3}>
            {integrationsData.integrations.map((integration) =>
              integration.integrationProvider.attributes?.user.map((attribute) => (
                <>
                  <InfoSection
                    title={attribute.name}
                    value={
                      user.attributes.find(
                        (ua) => toId(ua.integration) === toId(integration) && ua.key === attribute.key
                      )?.value || "(not set)"
                    }
                  />
                  <Divider />
                </>
              ))
            )}
          </PaperBox>
        </>
      )}
      <FieldSectionHeading mb={0.5}>Internal notes</FieldSectionHeading>
      <Caption mb={1.5}>Only users with Regional Manager permission level and above can view and leave notes.</Caption>
      <PersonNotes user={user} />
    </>
  )
}

export { PersonDetails }
